import { useEffect, useState } from 'react';
import { Wordmark } from '../../components/Wordmark.jsx';
import { APP_DOWNLOAD_URL } from '../../config/index.js';
import { brandPlane } from '../../theme/tokens.js';

/**
 * Web fallback for the two links Crave sends by email.
 *
 * On an iPhone with Crave installed, iOS never loads this page — Universal
 * Links intercept the URL and open the app directly. This page is for two
 * cases only: a desktop browser (where the scheme does nothing), and an iPhone
 * without the app installed.
 *
 * /verify-email?token=…   Redeems the token here and reports the result.
 * /reset-password?token=… Shows an "open in Crave" button; password entry
 *                         lives in the app, not here.
 */
export function EmailActionPage({ action }) {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? '';
  const deepLink = `crave://${action}?token=${encodeURIComponent(token)}`;

  if (action === 'verify-email') {
    return <VerifyPage token={token} deepLink={deepLink} />;
  }
  return <ResetPage deepLink={deepLink} />;
}

function VerifyPage({ token, deepLink }) {
  const [state, setState] = useState('loading'); // loading | success | error

  useEffect(() => {
    if (!token) {
      setState('error');
      return;
    }
    fetch('/api/auth/verify-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((r) => setState(r.ok ? 'success' : 'error'))
      .catch(() => setState('error'));
  }, [token]);

  return (
    <Shell>
      {state === 'loading' && <p style={s.body}>Confirming your email…</p>}

      {state === 'success' && (
        <>
          <h1 style={s.title}>Email confirmed</h1>
          <p style={s.body}>Your address is verified. Open the Crave app to continue.</p>
          <a href={deepLink} style={s.button}>
            Open Crave
          </a>
          {APP_DOWNLOAD_URL && (
            <p style={s.hint}>
              Don&apos;t have Crave yet?{' '}
              <a href={APP_DOWNLOAD_URL} style={s.link}>
                Get it on the App Store
              </a>
            </p>
          )}
        </>
      )}

      {state === 'error' && (
        <>
          <h1 style={s.title}>Link not valid</h1>
          <p style={s.body}>
            This link has expired or has already been used. Sign in to Crave and request a new one.
          </p>
          <a href={deepLink} style={s.button}>
            Open Crave
          </a>
        </>
      )}
    </Shell>
  );
}

function ResetPage({ deepLink }) {
  return (
    <Shell>
      <h1 style={s.title}>Set a new password</h1>
      <p style={s.body}>
        This link opens in the Crave app on your iPhone, where you can choose your new password.
      </p>
      <a href={deepLink} style={s.button}>
        Open Crave
      </a>
      {APP_DOWNLOAD_URL && (
        <p style={s.hint}>
          Don&apos;t have Crave yet?{' '}
          <a href={APP_DOWNLOAD_URL} style={s.link}>
            Get it on the App Store
          </a>
        </p>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <header style={styles.head}>
          <Wordmark height={22} />
        </header>
        <div style={styles.body}>{children}</div>
      </div>
    </div>
  );
}

const s = {
  title: {
    margin: '0 0 10px',
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: -0.4,
    color: '#1A1C12',
  },
  body: { margin: '0 0 22px', fontSize: 15, lineHeight: 1.55, color: '#5A5F4A' },
  button: {
    display: 'block',
    padding: '14px 0',
    borderRadius: 12,
    background: brandPlane,
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    textAlign: 'center',
    textDecoration: 'none',
    marginBottom: 14,
  },
  hint: { margin: 0, fontSize: 13, color: '#8A8F7A', textAlign: 'center' },
  link: { color: '#5A5F4A', fontWeight: 600 },
};

const styles = {
  page: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    background: '#F5F3EE',
  },
  card: {
    width: '100%',
    maxWidth: 380,
    background: '#fff',
    borderRadius: 20,
    padding: '28px 24px 32px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
  },
  head: { marginBottom: 24 },
  body: {},
};
